import React, { useEffect, useRef } from 'react';

// Define types for our elements and particles
interface ElementBase {
  type: string;
  x: number;
  y: number;
  startScroll: number;
  endScroll: number;
  opacity: number;
  glow: number;
}

interface LineElement extends ElementBase {
  type: 'line';
  length: number;
  maxLength: number;
  angle: number;
  direction: 'horizontal' | 'vertical';
  offset: number;
  segments: {x: number, y: number}[]; // For snake-like segments
  targetX: number; // Target position to move toward
  targetY: number;
  speed: number;
}

interface SquareElement extends ElementBase {
  type: 'square';
  size: number;
  maxSize: number;
  rotation: number;
  rotationSpeed: number;
  pulseSpeed: number;
  glowIntensity: number;
  orbitRadius: number;
  orbitSpeed: number;
  orbitAngle: number;
}

type Element = LineElement | SquareElement;

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
}

const ArchitecturalBackground = ({ numberOfElements = 5 }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Helper: Throttle function to optimize scroll handling
  const throttle = <T extends (...args: any[]) => void>(fn: T, wait: number) => {
    let lastTime = 0;
    return (...args: Parameters<T>) => {
      const now = Date.now();
      if (now - lastTime >= wait) {
        fn(...args);
        lastTime = now;
      }
    };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Use different context settings based on device
    const isMobile = window.innerWidth < 768;
    const ctx = canvas.getContext('2d', {
      alpha: false,
      willReadFrequently: false,
      desynchronized: true  // Better performance, less artifacts
    });
    if (!ctx) return;
    
    // CRITICAL: For mobile, apply a special background style
    if (isMobile) {
      // Apply black background to the canvas element directly for mobile
      canvas.style.backgroundColor = '#000000';
    }
    
    const dpr = window.devicePixelRatio || 1;
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.scale(dpr, dpr);

    // State variables
    let elements: Element[] = [];
    let particles: Particle[] = [];
    let scrollPosition = 0;
    let mousePosition = { x: 0, y: 0 };

    // Generate elements (lines and squares)
    const generateElements = (): Element[] => {
      const count = isMobile ? 5 : numberOfElements * 2; // Double the count and increased mobile count
      const newElements: Element[] = [];
      
      for (let i = 0; i < count; i++) {
        const startScroll = Math.random() * 0.6 - 0.3;
        const endScroll = Math.min(startScroll + 0.3, 1);
        
        // Create snake-like lines that can form squares
        const direction = Math.random() > 0.5 ? 'horizontal' : 'vertical';
        const angle = direction === 'horizontal' ? 0 : 90;
        const x = Math.random() * window.innerWidth;
        const y = Math.random() * window.innerHeight;
        
        // Create initial segments (snake body)
        const segments = [];
        const segmentCount = 40; // Number of segments in the snake
        
        for (let j = 0; j < segmentCount; j++) {
          segments.push({x, y});
        }
        
        newElements.push({
          type: 'line',
          x,
          y,
          length: 0,
          maxLength: 200 + Math.random() * 700, // Much longer lines
          angle,
          direction,
          offset: 0,
          segments,
          targetX: Math.random() * window.innerWidth,
          targetY: Math.random() * window.innerHeight,
          speed: 0.5 + Math.random() * 1.5,
          startScroll,
          endScroll,
          opacity: 0.8, // Brighter lines
          glow: 30 + Math.random() * 15, // Increased glow
        });
        
        // Squares with varied brightness (keep as is)
        newElements.push({
          type: 'square',
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: 0,
          maxSize: 40 + Math.random() * 50,
          startScroll,
          endScroll,
          opacity: 0.2 + Math.random() * 0.3, // Reduced opacity
          glow: Math.random() > 0.7 ? 10 + Math.random() * 8 : 3 + Math.random() * 5, // Reduced glow
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() * 0.1 + 0.02) * (Math.random() > 0.5 ? 1 : -1), // Much slower rotation
          pulseSpeed: 0.1 + Math.random() * 0.3, // Much slower pulse
          glowIntensity: 2 + Math.random() * 5, // Reduced glow intensity
          orbitRadius: 5 + Math.random() * 15, // Smaller orbit radius
          orbitSpeed: (Math.random() * 0.03 + 0.01) * (Math.random() > 0.5 ? 1 : -1), // Much slower orbit
          orbitAngle: Math.random() * Math.PI * 2,
        });
      }
      return newElements;
    };

    // Generate particles with more reasonable speed
    const generateParticles = (): Particle[] => {
      const particleCount = isMobile ? 25 : 150; // Even fewer on mobile
      const newParticles: Particle[] = [];
      for (let i = 0; i < particleCount; i++) {
        // Reduce desktop speed (from 1.0 to 0.7) and mobile speed (from 0.5 to 0.3)
        const speedFactor = isMobile ? 0.3 : 0.7;
        newParticles.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          vx: (Math.random() * 2 - 1) * speedFactor,
          vy: (Math.random() * 2 - 1) * speedFactor,
          size: 1 + Math.random() * 3,
          opacity: 0.2 + Math.random() * 0.4,
        });
      }
      return newParticles;
    };

    // Significantly simplify the section-specific drawing to prevent gray haze
    const drawSectionSpecific = (scrollTop: number) => {
      // For mobile, completely disable the grid to prevent any haze
      if (isMobile) {
        return; // Skip grid drawing entirely on mobile
      }
      
      // Desktop-only grid effect
      const whySection = document.querySelector('#why');
      const whyTop = whySection instanceof HTMLElement ? whySection.offsetTop : 0;
      const demoTop = document.querySelector('#demo') instanceof HTMLElement 
        ? (document.querySelector('#demo') as HTMLElement).offsetTop 
        : Number.MAX_SAFE_INTEGER;
      
      // Show grid with fade in/out effects - desktop only
      const gridStartOffset = 300;
      const gridFadeDistance = 200;
      
      let gridOpacity = 0;
      
      if (scrollTop >= (whyTop - gridStartOffset) && scrollTop < demoTop) {
        if (scrollTop < whyTop) {
          gridOpacity = 1 - ((whyTop - scrollTop) / gridFadeDistance);
        } 
        else if (scrollTop > (demoTop - gridFadeDistance)) {
          gridOpacity = (demoTop - scrollTop) / gridFadeDistance;
        } 
        else {
          gridOpacity = 1;
        }
        
        gridOpacity = Math.max(0, Math.min(1, gridOpacity * 0.15)); // Reduced max opacity to 15%
        
        if (gridOpacity > 0.01) {
          // Use the offscreen canvas technique
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = window.innerWidth;
          tempCanvas.height = window.innerHeight;
          const tempCtx = tempCanvas.getContext('2d');
          
          if (tempCtx) {
            tempCtx.beginPath();
            const gridSize = 50;
            for (let i = 0; i < window.innerWidth; i += gridSize) {
              tempCtx.moveTo(i, 0);
              tempCtx.lineTo(i, window.innerHeight);
            }
            for (let i = 0; i < window.innerHeight; i += gridSize) {
              tempCtx.moveTo(0, i);
              tempCtx.lineTo(window.innerWidth, i);
            }
            tempCtx.strokeStyle = `rgba(255, 255, 255, 0.1)`;
            tempCtx.lineWidth = 0.5; // Thinner lines
            tempCtx.stroke();
            
            ctx.save();
            ctx.globalAlpha = gridOpacity;
            ctx.drawImage(tempCanvas, 0, 0);
            ctx.restore();
          }
        }
      }
    };

    // Fixed animation loop to restore visual elements
    const animate = () => {
      if (!canvas) return;
      
      // Use the same background for both mobile and desktop
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      
      // Apply the same gradient for mobile and desktop
      const gradient = ctx.createLinearGradient(0, 0, window.innerWidth, window.innerHeight);
      gradient.addColorStop(0, 'rgb(30, 0, 60)');
      gradient.addColorStop(0.5, 'rgb(20, 0, 40)');
      gradient.addColorStop(1, 'rgb(0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);

      // Add subtle radial gradient for both mobile and desktop
      const radialGradient = ctx.createRadialGradient(
        window.innerWidth * 0.5, window.innerHeight * 0.3, 0,
        window.innerWidth * 0.5, window.innerHeight * 0.3, window.innerWidth * 0.7
      );
      radialGradient.addColorStop(0, 'rgba(77, 105, 255, 0.05)');
      radialGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = radialGradient;
      ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      ctx.globalCompositeOperation = 'source-over';

      // Draw particles for both mobile and desktop
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > window.innerWidth) p.vx *= -1;
        if (p.y < 0 || p.y > window.innerHeight) p.vy *= -1;
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = 'white';
        ctx.fillRect(p.x, p.y, p.size, p.size);
        ctx.restore();
      });

      // Draw elements for both mobile and desktop
      elements.forEach((el) => {
        let animationProgress = (scrollPosition - el.startScroll) / (el.endScroll - el.startScroll);
        animationProgress = Math.max(0, Math.min(1, animationProgress));

        // Interactive glow based on mouse proximity
        let glow = el.glow;
        if (!isMobile) {
          const dx = mousePosition.x - el.x;
          const dy = mousePosition.y - el.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 100) {
            glow = el.glow + (20 * (1 - distance / 100));
          }
        }

        ctx.save();
        ctx.globalAlpha = el.opacity * animationProgress;
        ctx.shadowBlur = isMobile ? Math.min(glow, 10) : glow; // Reduced glow on mobile
        ctx.shadowColor = 'white';

        if (el.type === 'line') {
          const line = el as LineElement;
          
          // Snake movement logic
          // Calculate distance to target
          const dx = line.targetX - line.x;
          const dy = line.targetY - line.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // If close to target, pick a new target
          if (distance < 20) {
            // 25% chance to try to form a square corner by making a 90-degree turn
            if (Math.random() < 0.25) {
              if (line.direction === 'horizontal') {
                line.direction = 'vertical';
                line.angle = 90;
                // Keep current X position but choose new Y target
                line.targetX = line.x;
                line.targetY = Math.random() > 0.5 ? 
                  line.y + 200 + Math.random() * 200 : 
                  Math.max(0, line.y - 200 - Math.random() * 200);
              } else {
                line.direction = 'horizontal';
                line.angle = 0;
                // Keep current Y position but choose new X target
                line.targetY = line.y;
                line.targetX = Math.random() > 0.5 ? 
                  line.x + 200 + Math.random() * 200 : 
                  Math.max(0, line.x - 200 - Math.random() * 200);
              }
            } else {
              // Random new target
              line.targetX = Math.random() * window.innerWidth;
              line.targetY = Math.random() * window.innerHeight;
            }
          }
          
          // Move toward target
          const speed = line.speed * animationProgress;
          if (distance > 0) {
            line.x += (dx / distance) * speed;
            line.y += (dy / distance) * speed;
          }
          
          // Update segments (snake body)
          line.segments.unshift({x: line.x, y: line.y});
          if (line.segments.length > 20) { // Limit number of segments
            line.segments.pop();
          }
          
          // Draw the snake-like line
          ctx.beginPath();
          ctx.moveTo(line.segments[0].x, line.segments[0].y);
          
          for (let i = 1; i < line.segments.length; i++) {
            ctx.lineTo(line.segments[i].x, line.segments[i].y);
          }
          
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2 + animationProgress * 2;
          ctx.stroke();
        } else if (el.type === 'square') {
          const square = el as SquareElement;
          
          // Update rotation
          square.rotation += square.rotationSpeed;
          
          // Update orbit position
          square.orbitAngle += square.orbitSpeed;
          const orbitX = Math.cos(square.orbitAngle) * square.orbitRadius * animationProgress;
          const orbitY = Math.sin(square.orbitAngle) * square.orbitRadius * animationProgress;
          
          // Pulsing effect
          const pulsingFactor = Math.sin(Date.now() / 1000 * square.pulseSpeed) * 0.2 + 0.8;
          
          // Calculate size with animation progress
          const size = square.maxSize * animationProgress * pulsingFactor;
          
          ctx.save();
          ctx.globalAlpha = square.opacity * animationProgress;
          ctx.shadowBlur = glow; // Same glow on both mobile and desktop
          ctx.shadowColor = 'white';
          
          // Apply rotation and translation
          ctx.translate(square.x + orbitX, square.y + orbitY);
          ctx.rotate(square.rotation * Math.PI / 180);
          
          // Draw square 
          ctx.beginPath();
          ctx.rect(-size / 2, -size / 2, size, size);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
          ctx.lineWidth = 1.5;
          ctx.stroke();
          
          // Inner square with different size
          ctx.beginPath();
          ctx.rect(-size / 3, -size / 3, size * 2/3, size * 2/3);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
          ctx.lineWidth = 1;
          ctx.stroke();
          
          // Add corner accents
          const cornerSize = size / 6;
          
          // Top-left corner
          ctx.beginPath();
          ctx.moveTo(-size/2, -size/2);
          ctx.lineTo(-size/2 + cornerSize, -size/2);
          ctx.moveTo(-size/2, -size/2);
          ctx.lineTo(-size/2, -size/2 + cornerSize);
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2;
          ctx.stroke();
          
          // Top-right corner
          ctx.beginPath();
          ctx.moveTo(size/2, -size/2);
          ctx.lineTo(size/2 - cornerSize, -size/2);
          ctx.moveTo(size/2, -size/2);
          ctx.lineTo(size/2, -size/2 + cornerSize);
          ctx.stroke();
          
          // Bottom-left corner
          ctx.beginPath();
          ctx.moveTo(-size/2, size/2);
          ctx.lineTo(-size/2 + cornerSize, size/2);
          ctx.moveTo(-size/2, size/2);
          ctx.lineTo(-size/2, size/2 - cornerSize);
          ctx.stroke();
          
          // Bottom-right corner
          ctx.beginPath();
          ctx.moveTo(size/2, size/2);
          ctx.lineTo(size/2 - cornerSize, size/2);
          ctx.moveTo(size/2, size/2);
          ctx.lineTo(size/2, size/2 - cornerSize);
          ctx.stroke();
          
          ctx.restore();
        }
        
        ctx.restore();
      });

      // Skip section-specific drawing on mobile
      if (!isMobile) {
        drawSectionSpecific(window.scrollY);
      }

      // Continue the animation
      requestAnimationFrame(animate);
    };

    // Event handlers
    const handleScroll = throttle(() => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      scrollPosition = maxScroll > 0 ? window.scrollY / maxScroll : 0;
    }, 100);

    const handleMouseMove = (e: MouseEvent) => {
      mousePosition = { x: e.clientX, y: e.clientY };
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      mousePosition = { x: touch.clientX, y: touch.clientY };
    };

    const handleResize = () => {
      if (!canvas || !ctx) return;
      
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.scale(dpr, dpr);
      elements = generateElements();
      particles = generateParticles();
    };

    // Initialize elements and particles
    elements = generateElements();
    particles = generateParticles();

    // Start animation with direct call to animate
    const animationFrameId = requestAnimationFrame(animate);

    // Add event listeners
    window.addEventListener('scroll', handleScroll);
    if (!isMobile) {
      window.addEventListener('mousemove', handleMouseMove as EventListener);
    } else {
      window.addEventListener('touchmove', handleTouchMove as EventListener);
    }
    window.addEventListener('resize', handleResize);

    // Add special scroll handler for mobile
    if (isMobile) {
      const mobileScrollHandler = () => {
        // Instead of forcing pure black, use the same gradient
        if (canvas) {
          const mobileCtx = canvas.getContext('2d');
          if (mobileCtx) {
            // Use the same gradient on scroll
            const gradient = mobileCtx.createLinearGradient(0, 0, window.innerWidth, window.innerHeight);
            gradient.addColorStop(0, 'rgb(30, 0, 60)');
            gradient.addColorStop(0.5, 'rgb(20, 0, 40)');
            gradient.addColorStop(1, 'rgb(0, 0, 0)');
            mobileCtx.fillStyle = gradient;
            mobileCtx.fillRect(0, 0, canvas.width, canvas.height);
          }
        }
      };
      window.addEventListener('scroll', mobileScrollHandler);
      
      // Clean up mobile-specific handler
      return () => {
        // ... other cleanup
        window.removeEventListener('scroll', mobileScrollHandler);
      };
    }

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (!isMobile) {
        window.removeEventListener('mousemove', handleMouseMove as EventListener);
      } else {
        window.removeEventListener('touchmove', handleTouchMove as EventListener);
      }
      window.removeEventListener('resize', handleResize);
      
      // Cancel animation frame
      cancelAnimationFrame(animationFrameId);
      
      // Clear the canvas on unmount to prevent artifacts
      if (canvas) {
        const unmountCtx = canvas.getContext('2d');
        if (unmountCtx) {
          unmountCtx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
    };
  }, [numberOfElements]);

  return (
    <canvas
      ref={canvasRef}
      className="canvas-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -2,
        pointerEvents: 'none',
        backgroundColor: '#000000',
      }}
    />
  );
};

export default ArchitecturalBackground;